import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { eur } from "../lib/format";
import { CheckCircle2, Loader2, PawPrint } from "lucide-react";

export default function SuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paymentIntent = params.get("payment_intent");
  const orderRef = paymentIntent || sessionId;
  const [state, setState] = useState({ status: "checking" });
  const purchaseFired = useRef(false);

  useEffect(() => {
    if (!orderRef) {
      setState({ status: "error" });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = paymentIntent
          ? await api.post("/payments/confirm", { payment_intent_id: paymentIntent })
          : await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", ...data });
          if (!purchaseFired.current && !localStorage.getItem(`pc_purchase_${orderRef}`)) {
            purchaseFired.current = true;
            localStorage.setItem(`pc_purchase_${orderRef}`, "1");
            track("Purchase", {
              transaction_id: orderRef,
              value: data.amount,
              currency: (data.currency || "usd").toUpperCase(),
              content_ids: ["pelocero"],
              contents: [{ id: "pelocero", quantity: data.units || 1, price: data.amount / (data.units || 1) }],
              num_items: data.units || 1,
            });
          }
          return;
        }
      } catch (e) { /* reintenta */ }
      attempts += 1;
      if (attempts < 20) setTimeout(poll, 2000);
      else setState({ status: "timeout" });
    };
    poll();
  }, [orderRef]);

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="success-page">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl ring-1 ring-zinc-100 p-10 text-center">
        <span className="mx-auto w-12 h-12 rounded-full bg-brand flex items-center justify-center mb-6">
          <PawPrint className="w-5 h-5 text-white" />
        </span>
        {state.status === "checking" && (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-brand mx-auto mb-4" />
            <h1 className="font-display font-bold text-xl">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-zinc-500">One moment, we're verifying your order with Stripe.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-4" />
            <h1 className="font-display font-bold text-2xl" data-testid="success-title">
              Order confirmed{state.customer_name ? `, ${state.customer_name.split(" ")[0]}` : ""}!
            </h1>
            <p className="mt-3 text-sm text-zinc-500 leading-relaxed">
              Thank you for your purchase. Your PeloCero ({state.units} {state.units === 1 ? "unit" : "units"}) for{" "}
              <strong className="text-ink">{eur(state.amount)}</strong> is being prepared and will ship to the
              address you provided. You'll receive Stripe's confirmation by email.
            </p>
          </>
        )}
        {(state.status === "timeout" || state.status === "error") && (
          <>
            <h1 className="font-display font-bold text-xl">We're verifying your order</h1>
            <p className="mt-2 text-sm text-zinc-500 leading-relaxed">
              If your payment went through, you'll receive an email confirmation within a few minutes. Questions? Write to us.
            </p>
          </>
        )}
        <Link
          to="/"
          data-testid="success-back-link"
          className="mt-8 inline-block bg-ink text-white font-display font-semibold text-sm rounded-full px-8 py-3.5 hover:bg-zinc-800 transition-colors"
        >
          Back to the store
        </Link>
      </div>
    </div>
  );
}
